import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { fetchArray } from "entity";
import RodadaEntity from "entity/Rodada";
import { copiarInscritosDeRodada } from "entity/Inscricao/copiarInscritos";
import useNotification from "notification";
import { useIsAdmin, useUserUid } from "useAuth";
import MenuDialog from "components/dialog/MenuDialog";
import ConfirmDialog from "components/dialog/ConfirmDialog";
import WindowDialog from "components/dialog/WindowDialog";

/**
 * Componente reutilizável que encapsula o fluxo completo de cópia de inscritos entre rodadas.
 *
 * Fluxo:
 * 1. Exibe MenuDialog com rodadas disponíveis para seleção
 * 2. Ao selecionar, exibe ConfirmDialog para confirmar a operação
 * 3. Ao confirmar, executa a cópia e exibe feedback
 *
 * @param {{ uid: string, nome: string }} rodadaDestino - Rodada de destino
 * @param {boolean} open - Controla a abertura do dialog
 * @param {function} onClose - Callback ao fechar
 */
export default function CopiarInscritosDialog({ rodadaDestino, open, onClose }) {
    const { showSuccess, showWarning, showError } = useNotification();
    const isAdmin = useIsAdmin();
    const currentUserUID = useUserUid();

    const [rodadas, setRodadas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingRodadas, setLoadingRodadas] = useState(false);
    const [rodadaSelecionada, setRodadaSelecionada] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showProcessing, setShowProcessing] = useState(false);

    // Buscar rodadas disponíveis ao abrir
    const carregarRodadas = useCallback(async () => {
        if (!rodadaDestino) return;

        setLoadingRodadas(true);
        try {
            const query = RodadaEntity.collection.where("status", "==", "ATIVO");
            const todasRodadas = await fetchArray(query.get());

            // Filtrar: remover a rodada destino e verificar permissões
            const rodadasFiltradas = todasRodadas.filter((r) => {
                // Não listar a própria rodada destino
                if (r.uid === rodadaDestino.uid) return false;

                // Admin vê todas
                if (isAdmin) return true;

                // Usuário comum: verificar perfisAutorizado
                if (r.perfisAutorizado && Object.keys(r.perfisAutorizado).length) {
                    return !!r.perfisAutorizado[currentUserUID];
                }

                // Rodada sem perfisAutorizado restritos
                return true;
            });
// console.log('rodadasFiltradas',rodadasFiltradas)
            setRodadas(
                rodadasFiltradas.map((r) => ({
                    id: r.uid,
                    label: r.nome+" - "+r.dataInicio,
                    dados: r,
                }))
            );
        } catch (error) {
            console.error("Erro ao carregar rodadas:", error);
            showError("Erro ao carregar rodadas disponíveis");
        } finally {
            setLoadingRodadas(false);
        }
    }, [rodadaDestino, isAdmin, currentUserUID, showError]);

    useEffect(() => {
        if (open) {
            carregarRodadas();
            setRodadaSelecionada(null);
            setShowConfirm(false);
            setShowProcessing(false);
        }
    }, [open, carregarRodadas]);

    // Ao selecionar uma rodada no MenuDialog
    const handleSelecionarRodada = (item) => {
        if (!item) {
            onClose();
            return;
        }
        setRodadaSelecionada(item);
        setShowConfirm(true);
    };

    // Ao confirmar a cópia
    const handleConfirmar = async (confirmou) => {
        setShowConfirm(false);

        if (!confirmou) {
            setRodadaSelecionada(null);
            return;
        }

        setShowProcessing(true);
        setLoading(true);

        try {
            const resultado = await copiarInscritosDeRodada(rodadaSelecionada.id, {
                uid: rodadaDestino.uid,
                nome: rodadaDestino.nome,
            });

            if (resultado.semInscritos) {
                showWarning("A rodada selecionada não possui inscritos");
            } else if (resultado.copiados === 0 && resultado.ignorados > 0) {
                showWarning(`Nenhum inscrito copiado. ${resultado.ignorados} já existiam na rodada destino`);
            } else {
                let msg = `${resultado.copiados} inscrito(s) copiado(s) com sucesso`;
                if (resultado.ignorados > 0) {
                    msg += `. ${resultado.ignorados} ignorado(s) (já inscritos)`;
                }
                showSuccess(msg);
            }
        } catch (error) {
            console.error("Erro ao copiar inscritos:", error);
            showError("Erro ao copiar inscritos. Tente novamente.");
        } finally {
            setLoading(false);
            setShowProcessing(false);
            onClose();
        }
    };

    // Fechar tudo
    const handleClose = () => {
        if (!loading) {
            setRodadaSelecionada(null);
            setShowConfirm(false);
            setShowProcessing(false);
            onClose();
        }
    };

    if (!open || !rodadaDestino) return null;

    return (
        <>
            {/* Dialog de processamento */}
            {showProcessing && (
                <WindowDialog
                    title="Importando Inscritos"
                    open={showProcessing}
                    maxWidth="xs"
                    hideCloseButton
                >
                    {() => (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 4, gap: 2 }}>
                            <CircularProgress />
                            <Typography>Copiando inscritos, aguarde...</Typography>
                        </Box>
                    )}
                </WindowDialog>
            )}

            {/* Dialog de confirmação */}
            {showConfirm && rodadaSelecionada && (
                <ConfirmDialog
                    title="Confirmar Importação"
                    open={true}
                    onClose={handleConfirmar}
                    labelYes="Confirmar"
                    labelNo="Cancelar"
                >
                    Deseja copiar os inscritos da rodada{" "}
                    <strong>{rodadaSelecionada.label}</strong> para{" "}
                    <strong>{rodadaDestino.nome}</strong>?
                </ConfirmDialog>
            )}

            {/* Dialog de seleção de rodada */}
            {!showConfirm && !showProcessing && (
                <MenuDialog
                    title="Importar Inscritos"
                    menuItems={loadingRodadas ? [] : rodadas}
                    showFilter={true}
                    emptyMessage={loadingRodadas ? "Carregando rodadas..." : "Nenhuma rodada disponível"}
                    open={true}
                    dialogWidth="md"
                    onClose={handleSelecionarRodada}
                />
            )}
        </>
    );
}
